import React, { Component } from 'react'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import { AlertModal } from 'app/common/modals/alert'
import type { DeviceCheck, RestaurantShortDTO } from 'shared/types'
import { Recaptcha } from 'app/common/recaptcha/Recaptcha'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import { restaurantServices } from 'shared/utils'
import { ChooseOrderTypeModal } from '../choose-order-type'

type Props = {
    closeAllAlerts: () => void,
    goTo: string,
    restaurant?: RestaurantShortDTO,
    currentOrder?: {
        restaurant: RestaurantShortDTO,
        collectMode: string,
        id?: string,
        token?: string
    },
    pickUpType?: string,
    onDeliverySet?: () => void,
    setCollectMode: (collectMode: string) => void,
    resetCart: () => void,
    setRestaurant: (restaurant: RestaurantShortDTO) => void,
    isRestaurantAvailable: (restaurantId: string) => boolean,
    fetchRestaurantData: (frNumber: string) => RestaurantShortDTO,
    fetchCatalog: (id: string) => void,
    checkRestaurant: (restaurantId?: string) => Promise<?RestaurantShortDTO>,
    checkDelivery: (
        restaurant?: RestaurantShortDTO,
        pickUpType?: string
    ) => boolean,
    cancelOrder: (
        id: string,
        token: string,
        deviceCheckPayload: DeviceCheck
    ) => void,
    navigate: (to: string) => void,
    hasCart: boolean,
    productsInCart: Array<PurchasableItem>,
    removeProductFromCart: (product: PurchasableItem) => void,
    intl: IntlShape
}
type State = {
    alert: boolean,
    catalogError: boolean,
    showCaptcha: boolean,
    showChooseOrderTypeModal: boolean,
    chooseOrderTypeModalHasBeenOpened: boolean
}

class _CCAlertComponent extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            alert: true,
            catalogError: false,
            showCaptcha: false,
            showChooseOrderTypeModal: false,
            chooseOrderTypeModalHasBeenOpened: false
        }
        this.initRecaptchaPromise()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    continueOrder = async () => {
        const {
            currentOrder,
            checkRestaurant,
            checkDelivery,
            navigate,
            intl
        } = this.props
        this.setState({ alert: false })
        const {
            restaurant: restaurantCurrentOrder,
            collectMode: collectModeCurrentOrder
        } = currentOrder || {}
        const { id } = restaurantCurrentOrder || {}
        const updatedRestaurant =
            restaurantCurrentOrder && (await checkRestaurant(id))
        if (!!updatedRestaurant) {
            collectModeCurrentOrder &&
                checkDelivery(updatedRestaurant, collectModeCurrentOrder)
            navigate(
                intl.formatMessage({
                    id: 'routes.clickandcollect.catalog'
                })
            )
        }
    }

    handleCcChoice = async () => {
        const {
            navigate,
            closeAllAlerts,
            setRestaurant,
            restaurant,
            setCollectMode,
            pickUpType,
            checkRestaurant,
            checkDelivery,
            goTo,
            intl
        } = this.props

        this.setState({ alert: false })

        if (restaurant) {
            const updatedRestaurant =
                restaurant && (await checkRestaurant(restaurant.id))
            if (!!updatedRestaurant) {
                setRestaurant(updatedRestaurant)
                const deliveryAvailable =
                    pickUpType && checkDelivery(updatedRestaurant, pickUpType)
                deliveryAvailable &&
                    pickUpType &&
                    (await setCollectMode(pickUpType))
                pickUpType
                    ? await this.fetchCatalogAndNavigate(updatedRestaurant.id)
                    : navigate(goTo)
            } else {
                closeAllAlerts()
            }
        } else {
            navigate(
                intl.formatMessage({
                    id: 'routes.clickandcollect.restaurant.choice'
                })
            )
        }
    }

    startNewOrder = async () => {
        const {
            resetCart,
            restaurant,
            currentOrder,
            hasCart,
            productsInCart,
            removeProductFromCart
        } = this.props
        const { chooseOrderTypeModalHasBeenOpened } = this.state

        if (hasCart) {
            productsInCart.forEach(product => removeProductFromCart(product))
        }
        const { id, token } = currentOrder || {}
        if (id && token) {
            await this.cancelPreviousOrder(id, token)
        }

        resetCart()

        if (
            restaurant &&
            restaurant.services.includes(restaurantServices.TABLE_ORDER) &&
            !chooseOrderTypeModalHasBeenOpened
        ) {
            this.setState({ showChooseOrderTypeModal: true })
            return
        }

        await this.handleCcChoice()
    }

    cancelPreviousOrder = async (id: string, token: string) => {
        const { cancelOrder } = this.props
        try {
            this.setState({ showCaptcha: true })
            const recaptchaToken = await this.recaptchaPromise
            await cancelOrder(id, token, { queen: recaptchaToken })
        } catch (error) {
            console.log('Error cancelling order', error)
        } finally {
            this.setState({ showCaptcha: false })
            this.initRecaptchaPromise()
        }
    }

    fetchCatalogAndNavigate = async (restaurantId: string) => {
        const { onDeliverySet, fetchCatalog, closeAllAlerts } = this.props
        try {
            await fetchCatalog(restaurantId)
            closeAllAlerts()
            onDeliverySet && onDeliverySet()
        } catch (error) {
            this.setState({ catalogError: true })
        }
    }

    onRetry = async () => {
        const { currentOrder, checkRestaurant } = this.props
        const { restaurant: restaurantCurrentOrder } = currentOrder || {}
        this.setState({ catalogError: false })
        const { id } = restaurantCurrentOrder || {}
        if (id) {
            const updatedRestaurant = await checkRestaurant(id)
            updatedRestaurant &&
                (await this.fetchCatalogAndNavigate(updatedRestaurant.id))
        }
    }

    render() {
        const { currentOrder, closeAllAlerts, intl, restaurant } = this.props
        const { restaurant: restaurantCurrentOrder } = currentOrder || {}
        const {
            alert,
            catalogError,
            showCaptcha,
            showChooseOrderTypeModal
        } = this.state

        return (
            <div>
                {alert && (
                    <AlertModal
                        alertTitle={intl.formatMessage(
                            { id: 'ecommerce.continue.cart.title' },
                            {
                                ecommerceMode: ''
                            }
                        )}
                        alertContent={intl.formatMessage(
                            { id: 'ecommerce.continue.cart' },
                            {
                                restaurant: restaurantCurrentOrder
                                    ? restaurantCurrentOrder.name
                                    : '',
                                ecommerceMode: ''
                            }
                        )}
                        okLabel="clickandcollect.continue.cart.new"
                        leftLabel="clickandcollect.continue.cart.ok"
                        isOpen={alert}
                        handleClose={() => {
                            this.setState({ alert: false })
                            closeAllAlerts()
                        }}
                        onOkClick={this.startNewOrder}
                        onLeftClick={this.continueOrder}
                    />
                )}
                {catalogError && (
                    <AlertModal
                        alertTitleKey="oops"
                        alertContentKey="clickandcollect.search.restaurant.error.fetch.catalog"
                        leftLabel="app.error.button.cancel"
                        okLabel="app.error.button.retry"
                        isOpen={catalogError}
                        handleClose={() => {
                            this.setState({ catalogError: false })
                            closeAllAlerts()
                        }}
                        onLeftClick={() => {
                            this.setState({ catalogError: false })
                            closeAllAlerts()
                        }}
                        onOkClick={this.onRetry}
                    />
                )}
                {showCaptcha && (
                    <Recaptcha
                        action="WebCCAlert"
                        verifyCallback={this.verifyCallback}
                    />
                )}
                {showChooseOrderTypeModal && (
                    <ChooseOrderTypeModal
                        visible={showChooseOrderTypeModal}
                        close={() =>
                            this.setState({
                                showChooseOrderTypeModal: false,
                                alert: false
                            })
                        }
                        restaurant={restaurant}
                        handleCcClick={this.handleCcChoice}
                    />
                )}
            </div>
        )
    }
}

const CCAlertComponent = injectIntl(_CCAlertComponent)
export { CCAlertComponent }
